import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Container, Row, Col, Form, Alert, ListGroup, Breadcrumb, OverlayTrigger, Tooltip, Pagination } from 'react-bootstrap';
import Select from 'react-select';
import moment from 'moment';
import './offers.css';
import FloatingActionButton from '../../common_components/FloatingActionButton';
import AddOffer from './UserOfferAdd';
import { getPaginationItems } from '../../common_components/getPaginationItems';
import { debounce } from 'lodash';
import OfferDetails from './OfferDetails';

const AllOffers = () => {
    const [offers, setOffers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [storeBrands, setStoreBrands] = useState([]);
    const [filters, setFilters] = useState({
        nameFilter: '',
        selectedStoreBrand: '',
        favoriteStore: true, // Default to true
        favoriteProduct: false,
        favorites: false,
    });
    const [selectedOffer, setSelectedOffer] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);

    const token = localStorage.getItem('token');
    const navigate = useNavigate(); // useNavigate hook for navigation

    // State for pagination of offers
    const pageSize = 3; // Adjust page size as needed
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const fetchStoreBrands = useCallback(async () => {
        try {
            const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_API_URL}/highgarden/grocery-store-brands/`, {
                headers: { Authorization: `Token ${token}` }
            });
            const options = data.results.map(brand => ({
                value: brand.id,
                label: brand.name
            }));
            setStoreBrands(options);
        } catch (error) {
            console.error('Failed to fetch store brands', error);
        }
    }, [token]);

    // Store debounced function in useRef to maintain across renders
    const debouncedFetchRef = useRef(
        debounce(async (filters = {}, page = 1) => {
            try {
                setLoading(true);
                const queryFilters = {
                    search: filters.nameFilter || '',
                    store_brand_id: filters.selectedStoreBrand ? filters.selectedStoreBrand.value : '',
                    status: 'ACTIVE',
                    favorite_store: filters.favoriteStore || '',
                    favorite_product: filters.favoriteProduct || '',
                    favorites: filters.favorites || '',
                    page_size: pageSize,
                    page: page,
                };
                const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_API_URL}/highgarden/grocery-offers/`, {
                    params: queryFilters,
                    headers: { Authorization: `Token ${token}` },
                });
                setOffers(data.results);
                setTotalPages(Math.ceil(data.count / pageSize));
                setError('');
            } catch (error) {
                setError('Failed to fetch offers');
                console.error('Failed to fetch offers:', error);
            } finally {
                setLoading(false);
            }
        }, 300) // 300ms delay for debouncing
    );

    const fetchActiveOffers = useCallback((filters = {}, page = 1) => {
        debouncedFetchRef.current(filters, page);
    }, []);

    useEffect(() => {
        fetchActiveOffers();
        fetchStoreBrands();
    }, [fetchActiveOffers, fetchStoreBrands]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prevFilters) => {
            const newFilters = { ...prevFilters, [name]: value };
            fetchActiveOffers(newFilters);
            return newFilters;
        });
    };

    const handleToggleChange = (e) => {
        const { name, checked } = e.target;
        setFilters((prevFilters) => {
            const newFilters = { ...prevFilters, [name]: checked };
            if (name === 'favorites') {
                newFilters.favoriteStore = false;
                newFilters.favoriteProduct = false;
            } else if (name === 'favoriteStore' || name === 'favoriteProduct') {
                newFilters.favorites = false;
            }
            fetchActiveOffers(newFilters);
            return newFilters;
        });
    };

    const handleStoreBrandChange = (selectedOption) => {
        setFilters((prevFilters) => {
            const newFilters = { ...prevFilters, selectedStoreBrand: selectedOption || null };
            fetchActiveOffers(newFilters);
            return newFilters;
        });
    };

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        fetchActiveOffers(filters, pageNumber);
    };

    const renderOfferList = () => (
        <>
            <Breadcrumb>
                <Breadcrumb.Item active>All Offers</Breadcrumb.Item>
            </Breadcrumb>
            <Row>
                <Col md={7}>
                    <Form.Group className="mb-3">
                        <Form.Control
                            type="text"
                            placeholder="Search for products"
                            name='nameFilter'
                            value={filters.nameFilter}
                            onChange={handleFilterChange}
                        />
                    </Form.Group>
                </Col>
                <Col md={5}>
                    <Form.Group className="mb-3">
                        <Select
                            isClearable
                            placeholder="Store Brand"
                            value={filters.selectedStoreBrand}
                            options={storeBrands}
                            onChange={handleStoreBrandChange}
                        />
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Col>
                    <Form.Group className="mb-3 d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                            <OverlayTrigger
                                overlay={<Tooltip id="tooltip-favoriteStore">Filter offers from your favorite stores</Tooltip>}
                            >
                                <Form.Check
                                    type="switch"
                                    id="favoriteStore-switch"
                                    label="Offers from Favorite Stores"
                                    name="favoriteStore"
                                    checked={filters.favoriteStore}
                                    onChange={handleToggleChange}
                                />
                            </OverlayTrigger>
                            <OverlayTrigger
                                overlay={<Tooltip id="tooltip-navigate-favoriteStore">Navigate to favorites</Tooltip>}
                            >
                                <i
                                    className="bi bi-info-circle ml-2"
                                    style={{ cursor: 'pointer', marginLeft: '8px' }}
                                    onClick={() => navigate('/highgarden/favorites/stores')}
                                ></i>
                            </OverlayTrigger>
                        </div>
                        <div className="d-flex align-items-center">
                            <OverlayTrigger
                                overlay={<Tooltip id="tooltip-favoriteProduct">Filter offers on your favorite products</Tooltip>}
                            >
                                <Form.Check
                                    type="switch"
                                    id="favoriteProduct-switch"
                                    label="Offers on Favorite Products"
                                    name="favoriteProduct"
                                    checked={filters.favoriteProduct}
                                    onChange={handleToggleChange}
                                />
                            </OverlayTrigger>
                            <OverlayTrigger
                                overlay={<Tooltip id="tooltip-navigate-favoriteProduct">Navigate to favorites</Tooltip>}
                            >
                                <i
                                    className="bi bi-info-circle ml-2"
                                    style={{ cursor: 'pointer', marginLeft: '8px' }}
                                    onClick={() => navigate('/highgarden/favorites/products')}
                                ></i>
                            </OverlayTrigger>
                        </div>
                        <div className="d-flex align-items-center">
                            <OverlayTrigger
                                overlay={<Tooltip id="tooltip-favorites">Filter offers on all your favorites, either stores or products</Tooltip>}
                            >
                                <Form.Check
                                    type="switch"
                                    id="favorites-switch"
                                    label="All Favorites"
                                    name="favorites"
                                    checked={filters.favorites}
                                    onChange={handleToggleChange}
                                />
                            </OverlayTrigger>
                            <OverlayTrigger
                                overlay={<Tooltip id="tooltip-navigate-favorites">Navigate to all favorites</Tooltip>}
                            >
                                <i
                                    className="bi bi-info-circle ml-2"
                                    style={{ cursor: 'pointer', marginLeft: '8px' }}
                                    onClick={() => navigate('/highgarden/favorites')}
                                ></i>
                            </OverlayTrigger>
                        </div>
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                {loading ? (
                    <p>Loading offers...</p>
                ) : error ? (
                    <Alert variant="danger">{error}</Alert>
                ) : offers.length > 0 ? (
                    <>
                        <ListGroup>
                            {offers.map((offer) => (
                                <ListGroup.Item key={offer.id} action onClick={() => { setSelectedOffer(offer); }}>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <h5 className="d-flex align-items-center">
                                                {/* Icon to indicate if the offer belongs to the user */}
                                                {
                                                    offer.is_owner && (
                                                        <OverlayTrigger
                                                            overlay={<Tooltip id="tooltip-owner">Offer created by you</Tooltip>}
                                                        >
                                                            <i className="bi bi-person-fill" style={{ marginRight: '8px' }}></i>
                                                        </OverlayTrigger>
                                                    )
                                                }
                                                {offer.name}
                                                {offer.is_verified && (
                                                    <OverlayTrigger
                                                        overlay={<Tooltip id="tooltip-verified">Verified Offer</Tooltip>}
                                                    >
                                                        <i className="bi bi-check-circle-fill text-success ml-2" style={{ marginLeft: '8px' }}></i>
                                                    </OverlayTrigger>
                                                )}
                                            </h5>
                                            <p className="mb-1">{offer.store_brand_name}</p>
                                            <p className="mb-0">
                                                {offer.start_date ? moment(offer.start_date).format('DD-MMM-YYYY') : 'No start date available'}
                                                -
                                                {offer.end_date ? moment(offer.end_date).format('DD-MMM-YYYY') : 'No end date available'}
                                            </p>
                                        </div>
                                        <div>
                                            <p>{offer.products.length} Products</p>
                                        </div>
                                    </div>
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                        <div className="fixed-pagination">
                            <Pagination>
                                <Pagination.Prev
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                />
                                {getPaginationItems(currentPage, totalPages, 11, handlePageChange)}
                                <Pagination.Next
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                />
                            </Pagination>
                        </div>
                    </>
                ) : (
                    <Alert variant="info">No offers available at the moment.</Alert>
                )}
            </Row>
        </>
    );

    return (
        <Container padding="0">
            {selectedOffer ? (
                <OfferDetails
                    selectedOffer={selectedOffer}
                    setSelectedOffer={setSelectedOffer}
                />
            ) : (
                renderOfferList()
            )}
            <FloatingActionButton
                actions={[
                    {
                        icon: "bi bi-plus",
                        tooltipText: "Add an Offer",
                        handler: () => setShowAddModal(true),
                        tooltip: "Add an Offer",
                    }
                ]}
            />
            <AddOffer
                show={showAddModal}
                handleClose={() => setShowAddModal(false)}
            />
        </Container>
    );
};

export default AllOffers;
